import currency from "currency.js";

const configCurrencyVE = {
  fromCents: true,
  symbol: "Bs. ",
  decimal: ",",
  separator: ".",
};

// Configuration for different currencies based on API response
const currencyConfigs = {
  VES: {
    symbol: "Bs. ", // Add space for proper formatting
    decimal: ",",
    separator: ".",
    precision: 2,
    fromCents: true, // API returns value in cents, needs to be divided by 100
  },
  USD: {
    symbol: "US$ ",
    decimal: ".",
    separator: ",",
    precision: 2,
    fromCents: true, // API returns value in cents, needs to be divided by 100
  },
  USDT: {
    symbol: "₮ ",
    decimal: ".",
    separator: ",",
    precision: 6,
    fromCents: true,
  },
  CLP: {
    symbol: "CL$ ",
    decimal: ",",
    separator: ".",
    precision: 2,
    fromCents: true, // API returns value in cents, needs to be divided by 100
  },
};

/**
 * Converts a balance value to a formatted currency string.
 *
 * @param {number|null|undefined} balance - The balance value to convert.
 * @return {number} The converted balance value as a formatted currency string.
 */
export const convertFinalBalance = balance => {
  if (balance === null || balance === 0) {
    return Number("0,00");
  }
  const balanceInCurrency = Number(balance).toFixed(2); // Asegura dos decimales
  return Number(balanceInCurrency);
};

/**
 * Adds the given amount to the balance and returns the result formatted as a currency string.
 *
 * @param {number|string} balance - The initial balance.
 * @param {number|string} amount - The amount to add to the balance.
 * @return {string} The formatted balance after adding the amount.
 */
export const addBalance = (balance, amount) => {
  if (balance === null || amount === null) {
    return "0,00";
  }
  return currency(balance).add(amount).format();
};

/**
 * Subtracts the given amount from the balance and returns the result formatted as a currency string.
 *
 * @param {number|string} balance - The initial balance.
 * @param {number|string} amount - The amount to subtract from the balance.
 * @return {string} The formatted balance after subtracting the amount.
 */
export const subtractBalance = (balance, amount) => {
  if (balance === null || amount === null) {
    return "0,00";
  }

  return currency(balance, configCurrencyVE)
    .subtract(amount * 100)
    .format();
};

/**
 * Formats the given balance into a currency string.
 *
 * @param {number|string|null} balance - The balance to format.
 * @return {string} The formatted balance as a currency string.
 */
export const formatBalance = balance => {
  if (balance === null) {
    return "0,00";
  }
  const remainingBalance = currency(balance);

  return remainingBalance;
};

/**
 * Formats the given balance into a currency string.
 *
 * @param {number|string|null} balance - The balance to format.
 * @return {string} The formatted balance as a currency string.
 */
export const formatBalanceWithCurrency = balance => {
  if (balance === null) {
    return "0,00";
  }
  const remainingBalance = currency(balance, configCurrencyVE).format();

  return remainingBalance;
};

/**
 * Formats currency with proper thousands separator and precision based on currency type
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - The currency code (VES, USD, USDT, CLP)
 * @param {string} [customSymbol] - Optional custom symbol override
 * @return {string} Formatted currency string
 */
export const formatCurrencyAmount = (amount, currencyCode = "VES", customSymbol = null) => {
  const config = currencyConfigs[currencyCode] || currencyConfigs.VES;
  const symbol = customSymbol || config.symbol;

  if (amount === null || amount === undefined) {
    return `${symbol}0${"0".repeat(config.precision > 0 ? config.precision + 1 : 0)}`;
  }

  let formattedAmount = Number(amount);

  if (config.fromCents) {
    let base = 100;
    if (currencyCode === "USDT") {
      base = 1000000; // Crypto currencies should be divided by 1,000,000
    }
    formattedAmount = formattedAmount / base;
  }

  const currencyInstance = currency(formattedAmount, {
    symbol: "",
    decimal: config.decimal,
    separator: config.separator,
    precision: config.precision,
  });

  return `${symbol}${currencyInstance.format()}`;
};

/**
 * Formats balance from account object with proper currency formatting
 * @param {Object} account - Account object with balance and currency info
 * @return {string} Formatted balance string
 */
export const formatAccountBalance = account => {
  if (!account || account.balance === null || account.balance === undefined) {
    return "Bs.0,00";
  }

  return formatCurrencyAmount(account.balance, account.currency, account.symbol);
};

/**
 * Formats a number to display with K, M, B suffixes with smart rounding
 * @param {number} amount - The amount to format
 * @returns {string} - Formatted amount string
 */
export const formatAmountSuffix = amount => {
  if (!amount || amount < 0) return "0";

  const num = parseFloat(amount);

  if (num >= 1000000000) {
    const billions = num / 1000000000;
    return billions >= 100 ? `${Math.round(billions)}B` : `${billions.toFixed(1).replace(".0", "")}B`;
  }

  if (num >= 1000000) {
    const millions = num / 1000000;
    return millions >= 100 ? `${Math.round(millions)}M` : `${millions.toFixed(1).replace(".0", "")}M`;
  }

  if (num >= 1000) {
    const thousands = num / 1000;
    // Si es >= 100K (3 dígitos), redondear siempre
    return thousands >= 100 ? `${Math.round(thousands)}K` : `${thousands.toFixed(1).replace(".0", "")}K`;
  }

  return Math.round(num).toString();
};
